// Client-side helpers for fetching SID data directly from Azure Blob Storage
// (trading-data-v2/sid/), using the same container-level read+list SAS token as
// stockMetadataBlob.ts (GET /api/watchlist/sas-token). sid.zip is generated by
// processor/src/scheduler_task/2k_sid.rs and contains a single entry sid_all.csv:
//   Stock,{YYYYMM}...,price_chg,price,volume,value,ma20_above,ma22,ma30,
//   vol_ma22,vol_ma30,price_color,vol_color,trend,ma22_prev,ma30_prev,momentum,insider
// Month cells hold jumlah pemegang saham (angka bulat) or "-" when data is missing.

import { api } from '../services/api';
import { fetchZipEntry } from './zipRangeReader';

const SID_ROOT = 'sid/';

export interface SidResult {
  months: string[]; // YYYYMM ascending (dari header, contoh "202408")
  rows: Record<string, string | number>[];
  total: number;
}

function buildBlobUrl(path: string, containerUrl: string, sasToken: string): string {
  const isDev = (import.meta as any).env?.DEV || false;
  return isDev
    ? `/azure-blob-proxy/trading-data-v2/${path}?${sasToken}`
    : `${containerUrl}/${path}?${sasToken}`;
}

function parseSidCsv(csvText: string): SidResult {
  const lines = csvText.split('\n');
  if (!lines[0]) return { months: [], rows: [], total: 0 };

  const headers = lines[0].split(',').map(h => h.trim());
  const suffixStart = headers.indexOf('price_chg');
  const months = suffixStart > 1 ? headers.slice(1, suffixStart) : [];
  const monthSet = new Set(months);
  const rows: Record<string, string | number>[] = [];

  for (let i = 1; i < lines.length; i++) {
    const line = (lines[i] || '').trim();
    if (!line) continue;
    const values = line.split(',');

    const obj: Record<string, string | number> = {};
    headers.forEach((col, idx) => {
      const v = values[idx];
      if (col === 'Stock') {
        obj['Ticker'] = String(v ?? '');
      } else if (monthSet.has(col)) {
        // '-' = bulan tanpa data (saham belum listing / data hilang) — dipertahankan sbg string
        obj[col] = (v === undefined || v === '' || v === '-') ? '-' : Number(v);
      } else {
        obj[col] = Number(v) || 0;
      }
    });
    rows.push(obj);
  }

  return { months, rows, total: rows.length };
}

/**
 * Fetch sid/sid.zip (entry sid_all.csv) directly from Azure Blob.
 */
export async function fetchSidData(): Promise<{ success: true; data: SidResult }> {
  try {
    const tokenRes = await api.getStockMetadataSasToken();
    if (!tokenRes.success || !tokenRes.data) throw new Error(tokenRes.error);
    const { containerUrl, sasToken } = tokenRes.data;
    const zipUrl = buildBlobUrl(`${SID_ROOT}sid.zip`, containerUrl, sasToken);

    const buffer = await fetchZipEntry(zipUrl, 'sid_all.csv');
    if (!buffer) return { success: true, data: { months: [], rows: [], total: 0 } };

    const text = new TextDecoder().decode(buffer);
    return { success: true, data: parseSidCsv(text) };
  } catch (err: any) {
    console.error('Error fetching SID data:', err);
    return { success: true, data: { months: [], rows: [], total: 0 } };
  }
}
